import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Badge, Button, Card, CardHeader, ErrorState, PageHeader, PageLoader, useToast } from '../../../components/ui';
import { useCrumb } from '../../../components/layout/Breadcrumbs/Breadcrumbs';
import { InvoicePicker, PurchasePicker } from '../../../components/common/Pickers/Pickers';
import { KeyValues, ReasonDialog, TwoColumn } from '../../../components/common/Parts/Parts';
import { useAction } from '../../../hooks/useAction';
import { useFetch } from '../../../hooks/useFetch';
import { labelOf } from '../../../utils/labels';
import { PAYMENT_MODES, TXN_CATEGORIES } from '../../../constants/payments';
import { formatDate } from '../../../utils/date';
import { money } from '../../../utils/currency';
import { transactionService } from '../services/transactionService';
import type { ActionProps } from '../../../hooks/useAction';
import type { Transaction } from '../types/transactionTypes';

const REF_PATH = { invoice: '/invoices', purchase: '/purchases', expense: '/expenses', payslip: '/payroll' };
const PARTY_PATH = { customer: '/customers', vendor: '/vendors' };

/** Links an unused advance to an invoice (customer) or purchase (supplier). */
function AllocateCard({ t, run, busy, onDone }: ActionProps & { t: Transaction; onDone: () => void }) {
  const [target, setTarget] = useState<string | null>(null);
  const customer = t.partyType === 'customer';
  const save = async () => {
    if (!target) return;
    const body = customer ? { invoiceId: target } : { purchaseId: target };
    if (await run('allocate', () => transactionService.allocate(t._id, body), 'Advance adjusted against the bill')) onDone();
  };
  return (
    <Card>
      <CardHeader title="Adjust this advance" subtitle={`Use it to pay one ${customer ? 'invoice' : 'supplier bill'}`} />
      <div className="space-y-3 p-5 pt-4">
        {customer ? (
          <InvoicePicker label="Invoice" query={{ customerId: t.partyId }} value={target} onChange={(id) => setTarget(id)} />
        ) : (
          <PurchasePicker label="Supplier bill" query={{ vendorId: t.partyId }} value={target} onChange={(id) => setTarget(id)} />
        )}
        <Button fullWidth disabled={!target} loading={busy === 'allocate'} onClick={save}>Adjust advance</Button>
      </div>
    </Card>
  );
}

export default function TransactionDetails() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { busy, run } = useAction(toast);
  const [reversing, setReversing] = useState(false);
  const { data: t, error, reload } = useFetch(() => transactionService.get(id), [id]);
  useCrumb(`/transactions/${id}`, t?.transactionNo);

  if (error) return <ErrorState error={error} onRetry={reload} />;
  if (!t) return <PageLoader />;

  const inflow = t.direction === 'credit';
  const canReverse = !t.isReversed && !t.reversalOf && t.refType !== 'expense';
  const unusedAdvance = t.category === 'advance' && t.refType === 'none' && !t.isReversed && !t.reversalOf;

  const reverse = async (reason) => {
    const res = await run('reverse', () => transactionService.reverse(id, reason), 'Payment reversed');
    setReversing(false);
    if (res) reload();
  };

  return (
    <>
      <PageHeader
        title={t.transactionNo}
        badge={
          t.isReversed ? <Badge tone="red" dot>Reversed</Badge> : t.reversalOf ? <Badge tone="yellow" dot>Reversal entry</Badge> : <Badge tone={inflow ? 'green' : 'neutral'} dot>{inflow ? 'Money in' : 'Money out'}</Badge>
        }
        subtitle={`${labelOf(TXN_CATEGORIES, t.category)} · ${formatDate(t.date)}`}
        actions={
          <>
            {canReverse && <Button variant="dangerText" onClick={() => setReversing(true)}>Reverse</Button>}
            {t.refType === 'expense' && <Button variant="outline" onClick={() => navigate(`/expenses/${t.refId}`)}>Open expense</Button>}
            <Button variant="outline" leftIcon="plus" onClick={() => navigate('/transactions/new')}>Record another</Button>
          </>
        }
      />
      {t.isReversed && <p className="mb-5 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">Reversed: {t.reverseReason}. Reports ignore this entry and its reversal.</p>}

      <TwoColumn
        main={
          <Card>
            <CardHeader title="Payment details" />
            <div className="p-5 pt-4">
              <KeyValues
                rows={[
                  ['Amount', <span className={inflow ? 'text-emerald-700' : undefined}>{inflow ? '+' : '−'} {money(t.amount)}</span>, { strong: true }],
                  ['Type', labelOf(TXN_CATEGORIES, t.category)],
                  ['Date', formatDate(t.date)],
                  ['Mode', labelOf(PAYMENT_MODES, t.mode)],
                  t.referenceNo && ['UTR / cheque no', t.referenceNo],
                  [
                    'Party',
                    PARTY_PATH[t.partyType] && t.partyId ? (
                      <Link to={`${PARTY_PATH[t.partyType]}/${t.partyId}`} className="text-brand-600 hover:underline">{t.partyName}</Link>
                    ) : (
                      t.partyName || '-'
                    ),
                  ],
                  REF_PATH[t.refType] && t.refId && ['Linked to', <Link to={`${REF_PATH[t.refType]}/${t.refId}`} className="text-brand-600 hover:underline">Open {t.refType === 'payslip' ? 'salary slip' : t.refType}</Link>],
                  t.reversalOf && ['Reversal of', <Link to={`/transactions/${t.reversalOf}`} className="text-brand-600 hover:underline">Original entry</Link>],
                  t.reversedBy && ['Reversed by', <Link to={`/transactions/${t.reversedBy}`} className="text-brand-600 hover:underline">Reversal entry</Link>],
                  t.note && ['Note', t.note],
                ]}
              />
            </div>
          </Card>
        }
        aside={
          unusedAdvance ? (
            <AllocateCard t={t} run={run} busy={busy} onDone={reload} />
          ) : (
            <Card className="p-5 text-sm text-ink-500">
              Payments are never deleted. To fix a mistake, reverse this entry and record the right one.
            </Card>
          )
        }
      />
      <ReasonDialog
        open={reversing}
        onClose={() => setReversing(false)}
        onConfirm={reverse}
        loading={busy === 'reverse'}
        title="Reverse this payment?"
        message="An opposite entry is added. Linked invoice or bill balances are updated."
        confirmLabel="Reverse payment"
      />
    </>
  );
}
